// src/components/intake/PlaidRow.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui";
import StarsIcon from "../../styles/StarsIcon";
import { mountStarsIcons } from "../../styles/stars-icons";
import { useAuth } from "../../context/AuthContext.jsx";
import { hasChatAccess } from "../../shared/_helpers/chatGate";
import { getIntake } from "../../shared/_services/intake.services";
import { GENERIC_ERROR } from "../onboarding/onboardingCopy";
import PlaidConsentRow from "../plaid/PlaidConsentRow";
import { usePlaidOffer } from "../plaid/usePlaidOffer";
import { LINKED_ACCOUNTS_PATH } from "../plaid/plaidNav";
import { LINKED_TITLE } from "../plaid/plaidCopy";
import { QUESTIONS_PATH } from "./intakeNav";
import {
  PLAID_ERROR,
  QP_PLAID_ATTENTION,
  QP_PLAID_ATTENTION_ANY,
  QP_PLAID_LINK,
  QP_PLAID_LINK_ANOTHER,
  QP_PLAID_MANAGE,
  QP_PLAID_NAMES,
} from "./intakeCopy";

// The Questions page's Plaid row (SP4 decision 20; SP6 spec § Screens per
// app → Web). Nothing linked: "Link an account with Plaid", consent first, as
// on Q5 (the questionnaire's own fill, no plan gate — R320). Banks linked:
// their names, "Manage linked accounts" → Profile, and "Link another account"
// only for a client with chat access (R309); a bank that needs a sign-in adds
// a line to Profile. `plaid` is the intake GET's, read on every entry to the
// page, so an unlink on Profile shows here on return; a link made here
// re-reads it for the new bank's name and hands it up (`onPlaid`) so the
// items below offer figures.
export default function PlaidRow({ plaid: initial, onPlaid }) {
  const { user } = useAuth();
  const [plaid, setPlaid] = useState(initial);
  const alive = useRef(false);
  useEffect(() => {
    alive.current = true;
    mountStarsIcons();
    return () => {
      alive.current = false;
    };
  }, []);
  const offer = usePlaidOffer({
    redirectPath: QUESTIONS_PATH,
    consent: initial.consent,
    onStatus: (status) => onPlaid?.({ ...plaid, ...status }),
    onLinked: async () => {
      try {
        const res = await getIntake();
        if (alive.current) setPlaid(res.data.plaid);
        onPlaid?.(res.data.plaid);
      } catch (_) {
        if (alive.current) setPlaid((p) => ({ ...p, linked: true }));
        onPlaid?.({ ...plaid, linked: true, consent: true });
      }
      return null;
    },
  });
  const names = plaid.names ?? [];
  return (
    <div
      role="group"
      aria-label={LINKED_TITLE}
      className="tw-grid tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-divider tw-bg-surface tw-px-6 tw-py-4"
    >
      {plaid.linked ? (
        <div className="tw-flex tw-items-start tw-gap-3">
          <StarsIcon
            name="bank"
            className="tw-mt-px tw-h-5 tw-w-5 tw-shrink-0 tw-text-text-accent"
          />
          <div className="tw-grid tw-gap-2">
            <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-text">
              {QP_PLAID_NAMES(names)}
            </p>
            {plaid.needsAttention && (
              <p role="status" className="tw-m-0 tw-text-text">
                <Button as={Link} to={LINKED_ACCOUNTS_PATH} variant="text">
                  {names.length === 1
                    ? QP_PLAID_ATTENTION(names[0])
                    : QP_PLAID_ATTENTION_ANY}
                </Button>
              </p>
            )}
            <div className="tw-flex tw-flex-wrap tw-gap-6">
              <Button as={Link} to={LINKED_ACCOUNTS_PATH} variant="text">
                {QP_PLAID_MANAGE}
              </Button>
              {hasChatAccess(user) && (
                <Button variant="text" onClick={offer.start}>
                  {QP_PLAID_LINK_ANOTHER}
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div>
          <Button variant="ghost" onClick={offer.start}>
            {QP_PLAID_LINK}
          </Button>
        </div>
      )}
      {offer.consentRow && (
        <PlaidConsentRow
          row={offer.consentRow}
          onConsent={offer.giveConsent}
          idPrefix="qp-plaid"
        />
      )}
      {offer.failed && (
        <p role="alert" className="tw-m-0 tw-text-sm tw-text-danger">
          {offer.failed === "plaid" ? PLAID_ERROR : GENERIC_ERROR}
        </p>
      )}
    </div>
  );
}
